import PdfPreviewModal, {PdfPreview} from "components/PdfPreviewModal";
import TenantName from 'components/TenantName';
import WaitingDots from 'components/WaitingDots';
import { Account } from 'models/Account';
import { Invoice } from 'models/Invoice';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, Card, CardBody } from 'reactstrap';
import { getInvoiceExport } from 'services/Api';
import { editBasicDataInvoice } from 'store/actions/Billing';
import { bindActionCreators } from 'redux';
import { RootState } from 'store/store';
import {getThemeUrl, useTheme} from 'theme';
import {useOfficeTranslation} from 'i18n/OfficeTranslation';
import InvoiceStateBadge from './InvoiceStateBadge';
import {useInvoiceFormatting} from './useInvoiceFormatting';

interface Props {
    invoice: Invoice;
    accounts: Account[]
}

interface Actions {
    editBasicDataInvoice: (data: any) => void;
}

const InvoiceInfoSummaryCard: React.FC<Props & Actions> = ( {
                                                      invoice,
                                                      accounts,
                                                      editBasicDataInvoice,
                                                  } ) => {
    const theme = useTheme();
    const {t} = useOfficeTranslation();

    const {currency, date} = useInvoiceFormatting();
    const [deadlineType, setDeadlineType] = useState<"fixed" | "pay_when_paid">(
        invoice.deadline_type === "pay_when_paid" ? "pay_when_paid" : "fixed"
    );
    useEffect(() => {
        setDeadlineType(invoice.deadline_type === "pay_when_paid" ? "pay_when_paid" : "fixed");
    }, [invoice.deadline_type]);

    const updateDeadlineType = (value: "fixed" | "pay_when_paid") => {
        setDeadlineType(value);
        editBasicDataInvoice({
            id: invoice.id,
            deadline_type: value,
        });
    };

    const pdfRequest = useRef(0);
    useEffect(() => {
        setPdfPreview(null);
        return () => { pdfRequest.current += 1; };
    }, [invoice.id]);
    const [pdfPreview, setPdfPreview] = useState<PdfPreview | null>(null);
    useEffect(() => {
        if (!pdfPreview) return;
        return () => URL.revokeObjectURL(pdfPreview.source);
    }, [pdfPreview]);
    const openPdf = async () => {
        const request = ++pdfRequest.current;
        try {
            const pdfData = await getInvoiceExport(invoice.id);
            if (request !== pdfRequest.current) return;
            const bytes = Uint8Array.from(atob(pdfData), character => character.charCodeAt(0));
            const source = URL.createObjectURL(new Blob([bytes], {type: "application/pdf"}));
            setPdfPreview({
                name: `${invoice.GetDisplayName()}.pdf`,
                source,
            });
        } catch (error) {
            console.error(error);
        }
    };

    if ( invoice === undefined ) {
        return <WaitingDots/>
    }

    return <><Card className="invoice-summary-card">
        <CardBody>
            <h4 className="invoice-company">
                <Link to={`/accounts/${invoice.customer}`}>{accounts.find(account => account.id === invoice.customer)?.legal_name}</Link>
            </h4>
            {invoice.supplier && <div className="invoice-on-behalf">
                {t("common.onBehalfOf")} <Link to={`/accounts/${invoice.supplier}`}>{accounts.find(account => account.id === invoice.supplier)?.legal_name}</Link>
            </div>}
            <div className="invoice-tenant text-muted">{t("common.tenant")}: <TenantName id={invoice.tenant} /></div>
            <div className="invoice-summary-total">
                <strong>{currency(invoice.CalcGrandTotal())}</strong>
                {(invoice.draft || invoice.quote) && <InvoiceStateBadge invoice={invoice} />}
            </div>
            <dl className="invoice-facts">
                {invoice.invoice_number && <div><dt>{t("invoice.invoiceInfoSummaryCard.invoiceNumber")}</dt><dd>{invoice.invoice_number}</dd></div>}
                <div><dt>{t("common.created")}</dt><dd>{date(invoice.GetCreatedDate())}</dd></div>
                {invoice.draft && !invoice.quote && <div>
                    <dt>{t("invoice.invoiceInfoSummaryCard.paymentDeadline")}</dt>
                    <dd><select aria-label={t("invoice.invoiceInfoSummaryCard.paymentDeadline")}
                        className="form-select form-select-sm" value={deadlineType}
                        onChange={event => updateDeadlineType(event.target.value as "fixed" | "pay_when_paid")}>
                        <option value="fixed">{t("common.fixedDeadline")}</option>
                        <option value="pay_when_paid">{t("common.payWhenPaid")}</option>
                    </select></dd>
                </div>}
                {!invoice.draft && !invoice.quote && <div>
                    <dt>{t(invoice.IsPayWhenPaid() ? "invoice.labels.paymentTerm" : "invoice.labels.deadline")}</dt>
                    <dd>{invoice.IsPayWhenPaid() ? t("common.payWhenPaid") : date(invoice.GetDeadlineDate())}</dd>
                </div>}
                {(invoice.draft || invoice.quote) && <div className="invoice-vat-setting">
                    <dt><label htmlFor="invoice-vat-reverse-charged">{t("invoice.invoiceContentCard.vatReverseCharged")}</label></dt>
                    <dd className="form-check form-switch">
                        <input id="invoice-vat-reverse-charged" className="form-check-input" type="checkbox" role="switch"
                            checked={invoice.vat_reverse_charged}
                            onChange={event => editBasicDataInvoice({id: invoice.id, vat_reverse_charged: event.target.checked})} />
                    </dd>
                </div>}
            </dl>
            <div className="invoice-summary-actions">
                {!invoice.draft && !invoice.quote && <a className="btn btn-outline-primary"
                    href={getThemeUrl(theme, "pay", `/?${btoa(`${invoice.id}*${invoice.token}`)}`)}>
                    <i className="mdi mdi-link-variant" aria-hidden="true" /> {t("invoice.invoiceInfoSummaryCard.payLink")}
                </a>}
                <Button outline color="primary" onClick={openPdf}>
                    <i className="mdi mdi-download" aria-hidden="true" /> {t("invoice.detail.pdf")}
                </Button>
            </div>
        </CardBody>
    </Card>
        <PdfPreviewModal preview={pdfPreview} onClose={() => setPdfPreview(null)} />
    </>;
}

const mapStateToProps = ( state: RootState ) => {
    return {};
};

const mapDispatchToProps = (dispatch: any) =>
    bindActionCreators({ editBasicDataInvoice }, dispatch);

export default connect( mapStateToProps, mapDispatchToProps )( InvoiceInfoSummaryCard );
